import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { ELoadStatus } from '../../../state/state.interface';
import { IAiReport } from '../ai-report-view/ai-report-view.interface';
import { aiReportToCommentHtml } from '../ai-report-view/ai-report-view.utilities';
import { AiReportCommentButtonService } from './ai-report-comment-button.service';
import { AiReportCommentButtonStore } from './ai-report-comment-button.store';

/**
 * Saves an AI report as the user's own comment on the item it is about, only on this explicit click; once added the
 * button stays done, so the same report is not posted twice.
 */
@Component({
  imports: [MatButton, MatIcon, MatProgressSpinner, TranslateModule],
  providers: [AiReportCommentButtonService, AiReportCommentButtonStore],
  selector: 'app-ai-report-comment-button',
  styleUrl: './ai-report-comment-button.component.scss',
  templateUrl: './ai-report-comment-button.component.html',
})
export class AiReportCommentButtonComponent {
  private readonly store = inject(AiReportCommentButtonStore);
  private readonly translate = inject(TranslateService);
  private readonly dialogRef: MatDialogRef<unknown> | null = inject(MatDialogRef, { optional: true });

  public readonly itemId: InputSignal<number> = input.required<number>();
  public readonly report: InputSignal<IAiReport> = input.required<IAiReport>();
  public readonly commentAdded: OutputEmitterRef<void> = output<void>();

  public readonly isAdding: Signal<boolean> = computed(() => this.store.status() === ELoadStatus.loading);
  public readonly isAdded: Signal<boolean> = computed(() => this.store.status() === ELoadStatus.success);

  constructor() {
    // Here and not in ngOnInit: toObservable needs the injection context.
    toObservable(this.isAdded)
      .pipe(filter(Boolean), takeUntilDestroyed())
      .subscribe(() => this.commentAdded.emit());

    this.keepDialogOpenWhileAdding();
  }

  public onAdd(): void {
    this.store.addComment({
      description: aiReportToCommentHtml(
        this.report(),
        this.translate.instant('page.artificialIntelligence.reportComment.attribution'),
      ),
      itemId: this.itemId(),
    });
  }

  /** Closing the dialog while saving would cancel the request and drop `commentAdded`. */
  private keepDialogOpenWhileAdding(): void {
    const { dialogRef } = this;

    if (!dialogRef) {
      return;
    }

    const disableClose: boolean = dialogRef.disableClose === true;

    effect(() => {
      dialogRef.disableClose = disableClose || this.isAdding();
    });
  }
}
